import type { components } from '@feedfm/clips-api-client';
import { unwrapList } from '@feedfm/clips-api-client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useDemoAppContext } from '../../pages/demo/DemoAppContext';
import Collection from './Collection';
import './Collections.css';
import * as styles from './Collections.styles';

const Collections = () => {
  const [collections, setCollections] = useState<
    components['schemas']['Collection'][]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [collectionIdsInput, setCollectionIdsInput] = useState<string>('');
  const { client, clientId } = useDemoAppContext();

  const fetchCollections = async () => {
    setLoading(true);
    setError(null);
    setHasAttemptedFetch(true);

    const requestBody: {
      clientId: string;
      country: string;
      timestamp: string;
      collectionIds?: number[];
    } = {
      clientId,
      country: navigator.language.split('-')[1] || 'US',
      timestamp: new Date().toISOString(),
    };

    // Parse collection IDs from input
    const ids = collectionIdsInput
      ? collectionIdsInput
          .split(',')
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id))
      : [];

    // Add collectionIds to request body if provided
    if (ids.length > 0) {
      requestBody.collectionIds = ids;
    }

    const response = await client.POST('/v1/collections', {
      body: requestBody,
    });
    setLoading(false);

    if (response.error) {
      setError(
        response.error.message ??
          'An error occurred while fetching collections',
      );
      return;
    }

    if (response.data) {
      if (response.data.success) {
        setCollections(unwrapList(response.data.data.collections));
      } else {
        setError(response.data.message ?? 'An error occurred');
      }
    }
  };

  // Helper to split collections into two columns
  const splitCollections = <T = unknown,>(collections: T[]) => {
    const col1: T[] = [];
    const col2: T[] = [];
    collections.forEach((collection, idx) => {
      if (idx % 2 === 0) {
        col1.push(collection);
      } else {
        col2.push(collection);
      }
    });
    return [col1, col2];
  };

  const handleBack = () => {
    setCollections([]);
    setHasAttemptedFetch(false);
    setError(null);
    setCollectionIdsInput('');
  };

  if (loading) {
    return (
      <Box className="collections-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="collections-error">
        {error}
      </Alert>
    );
  }

  return (
    <Paper elevation={3} className="collections-container">
      <Typography variant="h5" component="h2" gutterBottom>
        Collections (POST /collections)
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        This endpoint returns a list of all available collections.
      </Typography>
      {client.getToken() === '' || !hasAttemptedFetch ? (
        <Box className="collections-empty">
          <Typography gutterBottom>
            Click the button below to fetch collections
          </Typography>
          <TextField
            label="Collection IDs (comma-separated)"
            variant="outlined"
            size="small"
            value={collectionIdsInput}
            onChange={(e) => {
              setCollectionIdsInput(e.target.value);
            }}
            placeholder="e.g. 1, 2, 3"
            fullWidth
            margin="normal"
            helperText="Leave empty to fetch all collections"
          />
          <Button
            variant="contained"
            onClick={fetchCollections}
            className="collections-fetch-button"
            disabled={client.getToken() === ''}
          >
            Fetch Collections
          </Button>
        </Box>
      ) : collections.length === 0 ? (
        <Box className="collections-empty">
          <Typography gutterBottom>No collections available</Typography>
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={styles.backButtonContainer}>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          </Box>
          <Box className="collections-flexgrid">
            {splitCollections(collections).map((column, colIdx: number) => (
              <Box key={colIdx} className="collections-column">
                {column.map((collection) => (
                  <Collection
                    key={collection.collectionId}
                    collection={collection}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default Collections;
